/**
 * Alliance Combat Constants
 * Turn-based strategy combat for castle attacks between alliances.
 */

import { resolveUltimateAbilityCharacterId } from './mvmChildUltimateResolution'

export { resolveUltimateAbilityCharacterId }

/** Main attacker/defender can bring up to 10 characters; allied users up to 5 */
export const MAIN_PARTICIPANT_MAX_CHARACTERS = 10
export const ALLIED_PARTICIPANT_MAX_CHARACTERS = 5

/** Total standard points for special abilities (Water, Fire, Wind, Wood, Lightning) */
export const SPECIAL_ABILITY_TOTAL_POINTS = 20

/** Per use from that element’s pool (pools start at 4 each; total budget 20 per participant). */
export const SPECIAL_ABILITY_POINTS = {
  water: 4,
  fire: 4,
  wind: 4,
  wood: 4,
  lightning: 4,
} as const

/** Legacy alias — lightning uses same cost as other elements in hex combat. */
export const LIGHTNING_MIN_POINTS = 4

/** Character types for movement range */
export type CharacterType = 'human' | 'demigod' | 'god' | 'magician'

/** Movement range by character type */
export const MOVEMENT_RANGE: Record<CharacterType, number> = {
  human: 2,
  demigod: 3,
  god: 4,
  magician: 3,
}

/** Parent characters by type */
export const CHARACTER_BY_TYPE: Record<CharacterType, string[]> = {
  human: ['sakura', 'archera', 'korthas'],
  demigod: ['hayate', 'bruxus'],
  god: ['caeloria', 'valyra', 'solvarion', 'vitalis'],
  magician: ['motoko', 'bula', 'rena', 'finza', 'nytheris'],
}

/** Explicit movement range by parent general. */
export const GENERAL_MOVEMENT_RANGE: Record<string, number> = {
  sakura: 4,
  rena: 5,
  motoko: 3,
  hayate: 3,
  valyra: 5,
  bula: 4,
  korthas: 3,
  bruxus: 3,
  vitalis: 5,
  archera: 4,
  solvarion: 3,
  finza: 4,
  nytheris: 5,
  caeloria: 4,
}

/**
 * MvM hex BASIC attack max axial distance `{ min: ATTACK_HEX_MIN, max }` for kingdom generals.
 * Child fighters use {@link CHILD_ATTACK_HEX_MAX_RANGE} first, then fallback to mapped general via `resolveUltimateAbilityCharacterId`.
 */
export const GENERAL_ATTACK_HEX_MAX_RANGE: Record<string, number> = {
  sakura: 1,
  rena: 3,
  motoko: 2,
  hayate: 1,
  valyra: 2,
  bula: 3,
  korthas: 1,
  bruxus: 1,
  vitalis: 3,
  archera: 3,
  solvarion: 3,
  finza: 3,
  nytheris: 3,
  caeloria: 3,
}

/** Minimum axial distance for BASIC attacks (hex count from attacker to target inclusive). */
export const ATTACK_HEX_MIN = 1

/** Epic weapon effect `archerRangeSteps`: only applies to these generals (children resolve via ultimate map). */
export const RANGED_GENERALS = [
  'archera', 'solvarion', 'caeloria', 'valyra', 'motoko', 'rena', 'finza', 'bula',
]

const ARCHER_WEAPON_RANGE_GENERAL_SET = new Set(RANGED_GENERALS)

/** Explicit movement matches parent general GENERAL_MOVEMENT_RANGE (ultimate source). Legacy typo alias. */
export const CHILD_MOVEMENT_RANGE: Record<string, number> = {
  astrae: 3,
  aurelyn: 3,
  caladros: 5,
  draxis: 3,
  elowyn: 4,
  elyndra: 3,
  kaedor: 3,
  kaelithra: 3,
  kai: 5,
  lorcan: 3,
  morvessa: 4,
  nyrelle: 4,
  orienth: 5,
  orineth: 5,
  rexor: 3,
  saelis: 3,
  seraphae: 4,
  silmera: 5,
  tharion: 4,
  vaelor: 4,
  vireya: 5,
}

/**
 * Child BASIC attack hex max (`min` stays {@link ATTACK_HEX_MIN}). Overrides general table per child ID.
 * Current values mirror each child’s mapped general ({@link resolveUltimateAbilityCharacterId}); tune independently as needed.
 */
export const CHILD_ATTACK_HEX_MAX_RANGE: Record<string, number> = {
  astrae: 2,
  aurelyn: 2,
  caladros: 3,
  draxis: 3,
  elowyn: 3,
  elyndra: 3,
  enlyndra: 3,
  kaedor: 1,
  kaelithra: 1,
  kai: 3,
  lorcan: 1,
  morvessa: 3,
  nyrelle: 3,
  orienth: 2,
  orineth: 2,
  rexor: 3,
  saelis: 1,
  seraphae: 3,
  silmera: 3,
  tharion: 3,
  vaelor: 1,
  vireya: 3,
}

/** Fallback when id missing from CHILD_MOVEMENT_RANGE — children with implicit movement 5 */
export const CHILD_MOVEMENT_RANGE_5 = ['orineth', 'orienth', 'caladros', 'kai', 'silmera', 'vireya']

export const CHILD_MOVEMENT_RANGE_2: string[] = []

/** Elemental special ability users */
export const ELEMENTAL_ABILITY_USERS = {
  water: ['rena', 'bula'],
  fire: ['sakura', 'valyra'],
  wood: ['hayate'],
  wind: ['finza', 'archera'],
  lightning: ['motoko'],
} as const

/** Ultimate weapons (blacksmith) - same effect as character ultimate */
export const ULTIMATE_WEAPONS: Record<string, string> = {
  mantle_of_absolute_evasion: 'archera',
  cataclysm_breaker_carapace: 'korthas',
  horn_of_the_skyforged_charge: 'hayate',
  idol_of_withered_might: 'bruxus',
  sunheart_aegis: 'solvarion',
  damnation_paradox_mirror: 'caeloria',
  recipe_of_endless_repose: 'valyra',
  vital_surge: 'motoko',
  eclipse_backlash: 'bula',
  fateweaver_sigil: 'rena',
  voidflux_annihilator: 'finza',
  grim_leech_scythe: 'nytheris',
  bulwark_of_the_faithful: 'vitalis',
  bloodpetal_cataclysm: 'sakura',
}

export function normalizeUltimateWeaponId(weaponId: string | null | undefined): string {
  let w = (weaponId || '').toString().trim().toLowerCase().replace(/\s+/g, '_')
  if (w === 'idle_of_withered_might') w = 'idol_of_withered_might'
  return w
}

export function getUltimateWeaponCharacterKey(weaponId: string | null | undefined): string | null {
  const k = normalizeUltimateWeaponId(weaponId)
  return ULTIMATE_WEAPONS[k] ?? null
}

/** Pick weapon ID for epic weapon ultimate: prefer weapon tied to resolved general (children map to parent). */
/** Pick weapon ID for ultimate: prefer one matching characterId, else first slug that maps to an ultimate weapon. */
export function pickWeaponIdForUltimate(embeddedWeaponIds: string[] | undefined, characterId: string): string | null {
  if (!Array.isArray(embeddedWeaponIds) || embeddedWeaponIds.length === 0) return null
  const cid = resolveUltimateAbilityCharacterId(characterId || '')
  const match = embeddedWeaponIds.find((wid) => getUltimateWeaponCharacterKey(wid) === cid)
  if (match) return normalizeUltimateWeaponId(match)
  const anyMapped = embeddedWeaponIds.find((wid) => getUltimateWeaponCharacterKey(wid) != null)
  return anyMapped ? normalizeUltimateWeaponId(anyMapped) : null
}

/**
 * Castle siege (attacker victory) resource transfer — asymmetric, authoritative values live in the
 * backend (`mvmConstants.js`). The defeated owner loses {@link MVM_DEFEAT_RESOURCE_LOSS_PERCENT}; the
 * attackers collectively gain the smaller {@link MVM_VICTORY_RESOURCE_GAIN_PERCENT} (the rest is burned).
 * Not surfaced as a percentage in the UI.
 */
export const MVM_DEFEAT_RESOURCE_LOSS_PERCENT = 0.2
export const MVM_VICTORY_RESOURCE_GAIN_PERCENT = 0.1

/** Defeated user choice: stay = lose 90% resources */
export const DEFEAT_STAY_RESOURCE_LOSS_PERCENT = 0.9

/** Get movement range for a character */
export function getMovementRange(characterId: string): number {
  const id = (characterId || '').toLowerCase()
  if (GENERAL_MOVEMENT_RANGE[id] != null) return GENERAL_MOVEMENT_RANGE[id]
  if (CHILD_MOVEMENT_RANGE[id] != null) return CHILD_MOVEMENT_RANGE[id]
  if (CHILD_MOVEMENT_RANGE_5.includes(id)) return 5
  if (CHILD_MOVEMENT_RANGE_2.includes(id)) return 2
  for (const [type, chars] of Object.entries(CHARACTER_BY_TYPE)) {
    if (chars.includes(id)) return MOVEMENT_RANGE[type as CharacterType]
  }
  return 3 // default
}

/** Epic bow-style range extension applies when resolved general id is in {@link RANGED_GENERALS}. */
export function eligibleForArcherWeaponRangeBonus(characterId: string): boolean {
  const resolved = resolveUltimateAbilityCharacterId((characterId || '').toLowerCase())
  return ARCHER_WEAPON_RANGE_GENERAL_SET.has(resolved)
}

/** Get attack range for a character */
export function getAttackRange(characterId: string): { min: number; max: number } {
  const id = (characterId || '').toLowerCase()
  const resolved = resolveUltimateAbilityCharacterId(id)
  const raw =
    CHILD_ATTACK_HEX_MAX_RANGE[id] ??
    GENERAL_ATTACK_HEX_MAX_RANGE[resolved]
  const maxHex =
    raw != null && Number.isFinite(Number(raw))
      ? Math.max(ATTACK_HEX_MIN, Math.floor(Number(raw)))
      : ATTACK_HEX_MIN
  return { min: ATTACK_HEX_MIN, max: maxHex }
}

/** Characters with attack max beyond adjacent are blocked by mountain LOS like projectiles/magic. */
export function isRanged(characterId: string): boolean {
  return getAttackRange(characterId).max > ATTACK_HEX_MIN
}
